import { faker } from '@faker-js/faker';

import {
  formatSize,
  generateFrameName,
  tag,
  venueType,
} from 'mocks/server/data/generation/generateDetails';
import { IBaseEntity, ILiveEntity } from 'types/detailsEntityType';
import { EntityType } from 'types/entityType';
import { LiveStatus } from 'utils/statuses';

export const liveBaseEntity = (
  baseEntity: IBaseEntity,
  status: LiveStatus = LiveStatus.ACTIVE
): ILiveEntity => {
  return {
    ...baseEntity,
    status: status,
    hasDraft: false,
  };
};

export const baseEntity = (
  entityType: EntityType,
  spaceId: string = faker.string.numeric(10)
): IBaseEntity => {
  const currency = faker.finance.currency();
  const width = faker.number.int({ min: 800, max: 7680 });
  const height = faker.number.int({ min: 600, max: 4320 });

  const exampleOperatingHours = [
    {
      MONDAY: ['00:00 - 23:59'],
      TUESDAY: ['00:00 - 23:59'],
      WEDNESDAY: ['00:00 - 23:59'],
      THURSDAY: ['00:00 - 23:59'],
      FRIDAY: ['00:00 - 23:59'],
      SATURDAY: ['00:00 - 23:59'],
      SUNDAY: ['00:00 - 23:59'],
    },
    {
      MONDAY: ['08:00 - 19:59'],
      TUESDAY: ['08:00 - 19:59'],
      WEDNESDAY: ['08:00 - 19:59'],
      THURSDAY: ['08:00 - 19:59'],
      FRIDAY: ['08:00 - 19:59'],
      SATURDAY: ['08:00 - 19:59'],
      SUNDAY: ['08:00 - 19:59'],
    },
    {
      MONDAY: ['08:00 - 19:59'],
      TUESDAY: ['08:00 - 19:59'],
      WEDNESDAY: ['08:00 - 19:59'],
      THURSDAY: ['08:00 - 19:59'],
      FRIDAY: ['08:00 - 19:59'],
      SATURDAY: [],
      SUNDAY: [],
    },
    {
      MONDAY: ['16:00 - 23:59'],
      TUESDAY: ['00:00 - 01:59', '16:00 - 23:59'],
      WEDNESDAY: ['00:00 - 01:59', '16:00 - 23:59'],
      THURSDAY: ['00:00 - 01:59', '16:00 - 23:59'],
      FRIDAY: ['00:00 - 01:59', '16:00 - 23:59'],
      SATURDAY: ['00:00 - 01:59'],
      SUNDAY: [],
    },
  ];

  return {
    spaceId: spaceId,
    name: generateFrameName(),
    mediaOwnerName: 'Global',
    environment: 'UndergroundStation',
    formatSize: formatSize(),
    internetAccessAvailable: faker.datatype.boolean(),
    averageWeeklyImpactsAvailable: faker.number.int({ min: 1000, max: 100000 }),
    planningRateCpm: `${currency.symbol} ${faker.number.int({ min: 1, max: 100 })}`,
    currency: currency.code,
    audienceDataSource: faker.internet.url(),
    venueType: venueType(),
    auctionToSpotTimeMinutes: faker.number.int({ multipleOf: 15, min: 15, max: 60 }),
    allowedMimeTypes: `image/png;${faker.system.mimeType()}`,
    operatingHours: faker.helpers.arrayElement(exampleOperatingHours),
    tags: [tag(), tag(), tag()],
    latitude: faker.location.latitude(),
    longitude: faker.location.longitude(),
    zip: faker.location.zipCode(),
    nativeResolution: `${width}x${height}`,
    consolidatedResolutions: [`${width}x${height}`],
    width: width,
    height: height,
    entityType: entityType,
  };
};

export const baseEntityWithNullFields = (spaceId: string, entityType: EntityType): IBaseEntity => ({
  spaceId: spaceId,
  name: null,
  mediaOwnerName: null,
  environment: null,
  formatSize: null,
  internetAccessAvailable: null,
  averageWeeklyImpactsAvailable: null,
  planningRateCpm: null,
  currency: null,
  audienceDataSource: null,
  venueType: null,
  auctionToSpotTimeMinutes: null,
  allowedMimeTypes: null,
  operatingHours: null,
  tags: null,
  longitude: null,
  latitude: null,
  zip: null,
  nativeResolution: null,
  consolidatedResolutions: null,
  width: null,
  height: null,
  entityType: entityType,
});

export const baseEntityWithEmptyFields = (
  spaceId: string,
  entityType: EntityType
): IBaseEntity => ({
  spaceId: spaceId,
  name: '',
  mediaOwnerName: '',
  environment: '',
  formatSize: null,
  internetAccessAvailable: null,
  averageWeeklyImpactsAvailable: null,
  planningRateCpm: '',
  currency: '',
  audienceDataSource: '',
  venueType: null,
  auctionToSpotTimeMinutes: null,
  allowedMimeTypes: '',
  operatingHours: {
    MONDAY: [],
    TUESDAY: [],
    WEDNESDAY: [],
    THURSDAY: [],
    FRIDAY: [],
    SATURDAY: [],
    SUNDAY: [],
  },
  tags: [],
  longitude: null,
  latitude: null,
  zip: '',
  nativeResolution: '',
  consolidatedResolutions: [''],
  width: null,
  height: null,
  entityType: entityType,
});
